import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { CountryISO } from 'ngx-intl-tel-input';
import { SelectItem } from 'primeng/api/selectitem';
import { Observable } from 'rxjs/internal/Observable';
import { map } from 'rxjs/operators';
import { AuthenticationSelectors } from '../auth/state/authentication.selectors';
import { JobActions } from '../job/job.actions';
import { JobSelectors } from '../job/job.selectors';

@Component({
    selector: 'template-send-message',
    templateUrl: './preview-edit-template-send-message.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class PreviewEditTemplateSendMessageComponent implements OnInit, OnChanges {
    @Input() templateId;
    @Input() testMediaDevices;
    @Input() selectedTemplate;
    @Input() updatedModel;
    @Input() templateFileRef;
    @Input() isSourceChanged;
    @Input() locations;
    @Input() providers;
    @Input() reasons;
    @Input() appointmentResources;
    @Input() selectedUser;

    locationOptions: Array<SelectItem> = [];
    personLocationOptions: Array<SelectItem> = [];
    providerOptions: Array<SelectItem> = [];
    appointmentResourceOptions: Array<SelectItem> = [];
    reasonOptions: Array<SelectItem> = [];

    currentUser$: Observable<any>;
    sendTestMessage$: Observable<any>;
    public selectedCountryISO$: Observable<any>;
    loading$: Observable<any>;

    isEmailCorrect = true;
    preferredCountries: Array<CountryISO> = [CountryISO.UnitedStates, CountryISO.Denmark];
    containPets = false;
    form = new FormGroup({
        destinatorEmail: new FormControl('', Validators.email),
        destinatorSms: new FormControl(''),
        destinatorVoice: new FormControl('')
    });

    testData: any = {};
    containPersons = false;
    containAppointments = false;
    containAppointment = false;
    appointments = [];
    containTrackable = false;

    pipe = new DatePipe('en-US');

    validatedCountryISO = (country: string) => Object.values(CountryISO)
            .find(iso => iso === country)
        || CountryISO.UnitedStates;

    constructor(private authenticationSelectors: AuthenticationSelectors,
                private jobSelectors: JobSelectors,
                private store: Store<any>) {
        this.currentUser$ = this.authenticationSelectors.selectCurrentUser();
        // this.mediaDevices$ = this.previewEditSelector.selectGetMediaDevices();

        this.selectedCountryISO$ = this.currentUser$.pipe(
            map(user => user.userCountry as string || CountryISO.UnitedStates),
            map(country => country.toLowerCase()), // TODO: Remove when all users converted to lower case in DB
            map(this.validatedCountryISO)
        );

        this.sendTestMessage$ = this.jobSelectors.selectGetMessageSendSuccessful();
        this.loading$ = this.jobSelectors.selectLoading();
    }

    ngOnInit(): void {
        this.initOptions();
        this.initTestData();
    }

    initOptions(): void {
        // We do not expect reasons, providers, locations, resources for selected user to change while the dialog is open
        // So we only need to initialize the dropdown options once

        // TODO: Consider if it's possible just to configure the dropdowns which attributes of an item is label and which is value

        this.setReasons();
        this.setProviders();
        this.setAppointmentResources();
        this.setLocation();
    }

    // TODO: This can be hopefully be simplified and/or split in smaller functions
    initTestData(): void {
        this.testData = [];
        this.appointments = [];

        if (this.updatedModel !== undefined && Object.keys(this.updatedModel).length > 0) {
            const modelJson = JSON.stringify(this.updatedModel);
            this.containPersons = modelJson.indexOf('persons') !== -1;
            this.containAppointments = modelJson.indexOf('appointment') !== -1 || modelJson.indexOf('appointmentTime') !== -1;
            this.containTrackable = modelJson.indexOf('trackableItem') !== -1;
            Object.keys(this.updatedModel)
                .map(key => {
                    const model = this.updatedModel[key];
                    const matches = model !== undefined && typeof(model) === 'string' && model.indexOf('template-context') !== -1 ?
                        model.match(/(<template-context[^>]*>)([^<]*|\n)*?(<\/template-context>)/g) : 0;
                    let i = 0;
                    if (matches !== 0 && matches !== null) {
                        while (i !== matches.length) {
                            // For parameters of type account - we should provide test data
                            if (matches[i].indexOf('account.') === -1) {
                                let keyLabel: string;

                                if (matches[i].indexOf('person') !== -1) {
                                    const label: any = matches[i].match(/(person\.)([\w])*/g);
                                    if (label !== undefined && label !== null) {
                                       keyLabel = label[0];
                                    }
                                }

                                if (matches[i].indexOf('appointment') !== -1 || matches[i].indexOf('adaptive') !== -1) {
                                    let label: any = matches[i].match(/(appointment\.)([\w])*/g);

                                    if (label === undefined || label === null && matches[i].indexOf('adaptive') !== -1) {
                                        label = matches[i].match(/(#dates\.dayOfWeek)([\w])*/g);
                                    }
                                    keyLabel = label !== null ? label[0] : '';
                                }

                                if (matches[i].indexOf('pet') !== -1) {
                                    const label: any = matches[i].match(/(pet\.)([\w])*/g);
                                    keyLabel = label[0];
                                    this.containPets = true;
                                }

                                if (keyLabel !== undefined) {
                                    keyLabel = keyLabel
                                        .trim()
                                        .replace('Local', '');
                                    if (keyLabel.indexOf('appointmentTime') !== -1) {
                                        this.testData[keyLabel] = this.pipe.transform(new Date(), 'yyyy-MM-ddThh:mm:ss');
                                    } else if (keyLabel.indexOf('person') !== -1) {
                                        if (keyLabel.indexOf('firstName') !== -1) {
                                            this.testData[keyLabel] = 'John';
                                        } else if (keyLabel.indexOf('lastName') !== -1) {
                                            this.testData[keyLabel] = 'Doe';
                                        } else if (keyLabel.indexOf('prefix') !== -1) {
                                            this.testData[keyLabel] = 'Mr';
                                        }
                                    } else {
                                        // appointmentId is added for gateway email confirm
                                        if (keyLabel.indexOf('appointmentId') === -1) {
                                            this.containAppointment = true;
                                            if (keyLabel.indexOf('location') !== -1) {
                                               if (this.locationOptions.length > 0) {
                                                  this.testData['appointment.locationObj'] = this.locationOptions[0];
                                                  this.testData['appointment.locationId'] = this.locationOptions[0].value.locationId;
                                                  this.testData['appointment.location'] = this.locationOptions[0].value.location;
                                               }
                                            } else if (keyLabel.indexOf('providerId') !== -1) {
                                                this.testData[keyLabel] = this.providerOptions[0];
                                            } else if (keyLabel.indexOf('reasonId') !== -1) {
                                                this.testData[keyLabel] = this.reasonOptions[0];
                                            } else if (keyLabel.indexOf('resource') !== -1) {
                                                this.testData[keyLabel] = this.appointmentResourceOptions[0];
                                            } else {
                                                this.testData[keyLabel] = keyLabel.split('.')[1];
                                            }
                                        }
                                    }
                                }
                            }
                            i++;
                        }
                    } else {
                        this.testData['person.firstName'] = 'John';
                    }
                });

            if (this.selectedTemplate.communicationsCategoryId !== 'reminder' && this.containAppointments) {
                this.testData['appointment.appointmentTime'] = this.pipe.transform(new Date(), 'yyyy-MM-ddThh:mm:ss');
            }
            this.appointments.push(this.testData);
        } else {
            if (this.selectedTemplate.communicationsCategoryId !== 'reminder' && this.containAppointments) {
                this.testData['appointment.appointmentTime'] = this.pipe.transform(new Date(), 'yyyy-MM-ddThh:mm:ss');
            }
            this.testData['person.firstName'] = 'John';
            this.appointments.push(this.testData);
        }
    }

    sendTest(mediaType, selectedUser): void {
        const context: any = {};
        context.template = {};

        context.template = this.cleanUpUpdateModel(this.updatedModel);

        const contentType = `relation:${this.selectedTemplate.communicationsCategoryId}`;
        if (this.isAppointmentSupported()) {
            context.appointments = this.getAppointment();
        } else {
            context.person = this.getAppointment()[0];
        }

        if (this.containTrackable) {
            context.trackableItem = {};
            context.trackableItem.trackableItemLocation  = "Autoremind";
            context.trackableItem.trackableItemRemoteItemType= "Eyeglass";
            context.trackableItem.trackableItemRemoteItemId = "Order-1234"
        }

        const destinator = this.getDestinator(mediaType)
            .replace('+', '');

        const deviceId = this.testMediaDevices !== undefined ? this.testMediaDevices.find(media =>
                        media.device.indexOf(mediaType) !== -1).device : undefined;
        const templateId = this.isSourceChanged ? undefined : this.templateId;

        const templateFileRef = this.isSourceChanged ? 'tmp/' + this.templateFileRef : this.templateFileRef;

        this.store.dispatch(JobActions.sendTestMessage({context, userId: selectedUser.userId,
                                                        userName: selectedUser.userName, contentType,
                                                        templateFileRef, templateId,
                                                        destinator, mediaType, deviceId}));
    }

    isAppointmentSupported(): boolean {
       return this.selectedTemplate.communicationsCategoryId === 'reminder' ||
          this.selectedTemplate.communicationsCategoryId === 'recalls' ||
          this.selectedTemplate.communicationsCategoryId === 'missed' ||
          (this.selectedTemplate.communicationsCategoryId === 'triggered' && !this.containTrackable) ||
          this.containAppointments;
    }

// TODO: Keep test destinators in NgRx store so you don't have to enter again next time you open the dialog
    getDestinator(type): string {
        switch (type) {
            case 'email' :
                return `${this.form.controls.destinatorEmail.value}`;
            case 'sms' :
                return this.form.controls.destinatorSms.value !== null
                    ? `${this.form.controls.destinatorSms.value.e164Number}` : undefined;
            case 'voice' :
                return this.form.controls.destinatorVoice.value !== null
                    ? `${this.form.controls.destinatorVoice.value.e164Number}` : undefined;
            default:
                return undefined;
        }
    }

    getDevice(type): string {
        if (this.testMediaDevices !== undefined && this.testMediaDevices.length > 0) {

            return this.testMediaDevices.find(media => media.device.indexOf(type) !== -1) !== undefined
                ? this.testMediaDevices.find(media => media.device.indexOf(type) !== -1).device
                                        .split('|')[1] : 'default';
        }

        return 'default';
    }

    getAppointment(): any {
        const testAppointments = [];
        this.appointments.forEach((appointment: any) => {
            const testData = {};
            Object.entries(appointment)
                .map(tData => {
                    if (tData[1] instanceof Object) {
                        const value: any = tData[1];
                        testData[tData[0].split('.')[1]] = value.value;
                    } else {
                        testData[tData[0].split('.')[1]] = tData[1];
                    }
                });

            // adding appointment time for sendtest message when no appointment time provided
            if (Object.keys(testData)
                .findIndex(key => key === 'appointmentTime') === -1 && this.isAppointmentSupported()) {

                testData['appointmentTime'] = this.pipe.transform(new Date(), 'yyyy-MM-ddThh:mm:ss');
            }
            testAppointments.push(testData);
        });

        return testAppointments;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.updatedModel !== undefined) {
            this.initTestData();
        }

        if (changes.appointmentResources !== undefined ) {
            this.setAppointmentResources();
            this.initTestData();
        }

        if (changes.reasons !== undefined) {
            this.setReasons();
            this.initTestData();
        }

        if (changes.locations !== undefined ) {
            this.setLocation();
            this.initTestData();
        }

        if (changes.providers !== undefined) {
            this.setProviders();
            this.initTestData();
        }
    }

    setAppointmentResources(): any {
        if (this.appointmentResources) {
            this.appointmentResourceOptions = this.appointmentResources.map(resource => ({
                ...resource,
                label: resource.resourceName,
                value: resource.resourceName
            }));
        }
    }
    setReasons(): any {
        if (this.reasons) {
            this.reasonOptions = this.reasons.map(reason => ({
                ...reason,
                label: reason.reasonName,
                value: reason.reasonId
            }));
        }
    }

    setProviders(): any {
        if (this.providers) {
            this.providerOptions = this.providers.map(provider => ({
                label: `${provider.providerName} (remoteProviderId: ${provider.remoteProviderId})`,
                value: provider.providerId
            }));
        }
    }

    setLocation(): any {
        if (this.locations) {

            this.personLocationOptions = this.locations
               .filter(location => location.locationId !== null)
               .map(location => ({
               ...location,
               label: location.locationName,
               value: location.locationId
            }));

            this.locationOptions = this.locations.map(location => ({
                ...location,
                label: location.locationName,
                value: {location: location.locationName, locationId: location.locationId}
            }));
        }
    }

    setAppointmentLocation(appointment: any, value: any): void {
      if (value) {
         appointment['appointment.locationId'] = value?.locationId || undefined;
         appointment['appointment.location'] = value?.locationName || undefined;
         appointment['appointment.locationObj'] = value;
      } else {
         appointment['appointment.locationId'] = undefined;
         appointment['appointment.location'] = undefined;
         appointment['appointment.locationObj'] = undefined;
      }
    }

    addAppointment(): void {
        const appointment: any = {};
        // TODO: Consider just cloning the entire appointment object
        Object.entries(this.appointments[0])
            .forEach(tData => {
                if (tData[0].indexOf('appointment') !== -1) {
                    if (tData[0].indexOf('appointmentTime') !== -1) {
                        appointment[tData[0]] = tData[1];
                    }
                } else {
                    appointment[tData[0]] = tData[1];
                }
        });

        this.appointments.push(appointment);
    }

    // TODO: Split into a validator per input - e.g. an appointmentTime validator and the view can disable button based on form valid
    validateTestData(): boolean {
        let invalid = false;
        this.appointments.forEach(appointment => {
            if (invalid) {
                invalid = Object.values(appointment)
                    .find(value => value === '') !== undefined;
            }

            const index = Object.keys(appointment)
                .find(key => key.indexOf('appointmentTime') !== -1);
            if (index !== undefined) {
                const value = appointment[index];
                const matches = value.match(/(\d{4})-(\d\d)-(\d\d)T(\d\d):(\d\d):(\d\d)/g);
                if (matches === undefined || matches === null) {
                    invalid = true;
                }
            }
        });

        return invalid;
    }

    /**
     * This is helping to remove no breaking space character (&nbsp)
     * from every template variable in updateModel
     * fix-#18206336
     * @param updateModel: Record<string, string>
     */
    private cleanUpUpdateModel(updateModel: Record<string, string>): Record<string, string> {
      return Object.keys(updateModel)
          .reduce((updateModelClean, templateVariableKey) => {
        let variableContent: any = updateModel[templateVariableKey].toString();
        const replaceIf = templateVariableKey.includes('voice') ||
          templateVariableKey.includes('sms') ||
          templateVariableKey.includes('emailSubject');

        let templateVariable: any;
        if (variableContent) {
            variableContent = variableContent.replaceAll('contenteditable="false">', '>');
            variableContent = replaceIf
                ? variableContent
                    .replace(/&nbsp;/g, ' ')
                    .replace(/&/g, '&amp;')
                : variableContent;
            if (templateVariableKey === 'voiceMessage' && variableContent === 'Insert Text Here') {
                variableContent = '';
            }
            templateVariable = {...updateModelClean,
                [templateVariableKey]: variableContent
            };
          } else {
            templateVariable = {...updateModelClean,
                [templateVariableKey]: ''
            };
        }

        return templateVariable;
      }, {});
    }
}
